import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';

export default function PrivacyPolicy() {
    useEffect(() => {
        // Load Iubenda script if not already present
        const scriptId = 'iubenda-js';
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.type = 'text/javascript';
            script.src = 'https://cdn.iubenda.com/iubenda.js';
            document.body.appendChild(script);
        }

        // Restart Iubenda loader to find the new embed link
        if (window.Iubenda && window.Iubenda.loader) {
            window.Iubenda.loader.restart();
        }
    }, []);

    return (
        <div className="min-h-screen bg-white pt-32 pb-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-16"
                >
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-6">
                        <Shield className="w-8 h-8 text-blue-600" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">Informativa sulla Privacy</h1>
                    <p className="text-lg text-gray-600">
                        La tua privacy è importante per noi. Questa pagina riporta l'informativa ufficiale completa.
                    </p>
                </motion.div>

                {/* Content Container */}
                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm text-gray-700">
                    <div dangerouslySetInnerHTML={{
                        __html: `
                        <div class="container">
                            <header class="main-header">
                                <h1 class="text-3xl font-bold mb-4">Privacy Policy di <strong>www.tecnosys-ups.com</strong></h1>
                                <p class="mb-4">Benvenuto nella privacy policy di www.tecnosys-ups.com. Questa policy ti aiuterà a comprendere quali dati raccogliamo, perché li raccogliamo e quali sono i tuoi diritti in merito.</p>
                                <p class="text-sm text-gray-500">Ultima modifica: 26 gennaio 2026</p>
                            </header>

                            <main class="mt-12 space-y-12">
                                <section class="main__section section__summary">
                                    <h2 id="summary" class="text-2xl font-bold mb-6">Riepilogo</h2>
                                    <div class="summary__cards-wrapper grid md:grid-cols-2 gap-6">
                                        <article id="data-we-collect-card" class="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                                            <header>
                                                <h3 class="text-xl font-bold mb-2">Dati che raccogliamo automaticamente</h3>
                                                <p class="text-sm text-gray-600 mb-4">Raccogliamo automaticamente i tuoi dati, ad esempio quando visiti www.tecnosys-ups.com.</p>
                                            </header>
                                            <div class="flex flex-wrap gap-2 mb-6">
                                                <span class="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs">Dati di utilizzo</span>
                                                <span class="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs">risposte alle domande</span>
                                                <span class="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs">contenuti dell'utente</span>
                                            </div>
                                            <div class="summary__card-section how-we-use-section">
                                                <h4 class="font-bold mb-2">Come li usiamo</h4>
                                                <ul class="list-disc pl-5 text-sm space-y-1">
                                                    <li>Hosting ed infrastruttura backend</li>
                                                    <li>Interazione con le piattaforme di live chat</li>
                                                </ul>
                                            </div>
                                        </article>

                                        <article id="data-you-give-to-us-card" class="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                                            <header>
                                                <h3 class="text-xl font-bold mb-2">Dati che ci fornisce</h3>
                                                <p class="text-sm text-gray-600 mb-4">Raccogliamo i dati che ci fornisci, ad esempio quando interagisci con la nostra chat online.</p>
                                            </header>
                                            <div class="flex flex-wrap gap-2 mb-6">
                                                <span class="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs">nome</span>
                                                <span class="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs">cognome</span>
                                                <span class="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs">numero di telefono</span>
                                            </div>
                                            <div class="summary__card-section how-we-use-section">
                                                <h4 class="font-bold mb-2">Come li usiamo</h4>
                                                <ul class="list-disc pl-5 text-sm space-y-1">
                                                    <li>Interazione con le piattaforme di live chat</li>
                                                </ul>
                                            </div>
                                        </article>
                                    </div>
                                </section>

                                <section class="main__section section__owner-and-data-controller">
                                    <h2 id="owner-and-data-controller" class="text-2xl font-bold mb-4">Titolare del Trattamento dei Dati</h2> 
                                    <p class="mb-2"><strong>Tecnosys di Lo Verso Salvatore</strong><br>via A. Cesalpino, 7 - 90128<br>Palermo</p>
                                    <p><strong>Indirizzo email del Titolare:</strong> info@tecnosys-ups.com</p>
                                </section>

                                <section class="main__section section__types-of-data">
                                    <h2 id="types-of-data" class="text-2xl font-bold mb-4">Tipo di Dati che raccogliamo</h2>
                                    <p class="mb-4">Fra i Dati Personali raccolti da questa Applicazione, in modo autonomo o tramite terze parti, ci sono:</p>
                                    <div class="flex flex-wrap gap-2 mb-6">
                                        <span class="bg-gray-100 px-3 py-1 rounded-full text-sm">Dati di utilizzo</span>
                                        <span class="bg-gray-100 px-3 py-1 rounded-full text-sm">nome</span>
                                        <span class="bg-gray-100 px-3 py-1 rounded-full text-sm">cognome</span>
                                        <span class="bg-gray-100 px-3 py-1 rounded-full text-sm">numero di telefono</span>
                                        <span class="bg-gray-100 px-3 py-1 rounded-full text-sm">Strumenti di Tracciamento</span>
                                    </div>
                                    <p class="text-sm leading-relaxed">
                                        I Dati Personali possono essere liberamente forniti dall'Utente o, nel caso di Dati di Utilizzo, raccolti automaticamente durante l'uso di questa Applicazione. Se non diversamente specificato, tutti i Dati richiesti da questa Applicazione sono obbligatori. Se l’Utente rifiuta di comunicarli, potrebbe essere impossibile per questa Applicazione fornire il Servizio.
                                    </p>
                                </section>

                                <section class="main__section section__mode-and-place">
                                    <h2 id="mode-and-place" class="text-2xl font-bold mb-4">Modalità e luogo del trattamento</h2>
                                    <h3 class="text-xl font-bold mb-2">Modalità di trattamento</h3>
                                    <p class="mb-4">Il Titolare adotta le opportune misure di sicurezza volte ad impedire l’accesso, la divulgazione, la modifica o la distruzione non autorizzate dei Dati Personali.</p>
                                    <h3 class="text-xl font-bold mb-2">Luogo</h3>
                                    <p class="mb-4">I Dati sono trattati presso le sedi operative del Titolare ed in ogni altro luogo in cui le parti coinvolte nel trattamento siano localizzate.</p>
                                    <h3 class="text-xl font-bold mb-2">Periodo di conservazione</h3>
                                    <p>Se non diversamente indicato, i Dati sono conservati per il tempo richiesto dalla finalità per la quale sono stati raccolti.</p>
                                </section>

                                <section class="main__section section__purpose-of-processing">
                                    <h2 id="purpose-of-processing" class="text-2xl font-bold mb-4">Finalità del Trattamento</h2>
                                    <ul class="list-disc pl-5 space-y-2">
                                        <li>Hosting ed infrastruttura backend</li>
                                        <li>Interazione con le piattaforme di live chat</li>
                                    </ul>
                                </section>

                                <section class="main__section section__rights">
                                    <h2 class="text-2xl font-bold mb-4">Diritti dell’Utente (GDPR)</h2>
                                    <p class="mb-4">Gli Utenti hanno il diritto di accedere, rettificare, cancellare i propri dati o opporsi al trattamento.</p>
                                    <p>Per esercitare i tuoi diritti, contatta: <strong>info@tecnosys-ups.com</strong></p>
                                </section>
                            </main>

                            <footer class="mt-16 pt-8 border-t border-gray-200">
                                <p class="text-xs text-gray-400">
                                    Generato con <strong>Iubenda</strong>. Questo documento riguarda esclusivamente questa Applicazione.
                                </p>
                            </footer>
                        </div>
                    ` }} />
                </div>
            </div>
        </div>
    );
}
